import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ArrowRight, Info, Sparkles } from "lucide-react";

const BUDGET_OPTIONS = ["5000", "10000", "20000", "30000", "50000", "100000", "200000"];
const GOALS = ["More qualified leads", "Foot traffic / walk-ins", "Online sales", "Brand awareness", "Appointment bookings"];
const TONES = ["Professional", "Friendly", "Bold & confident", "Playful", "Luxury"];

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-zinc-500 font-medium">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

export default function BriefStep({ brief, snapshot, updateBrief, onBack, onBuild, busy }) {
  return (
    <div className="fade-up" data-testid="brief-step">
      <button onClick={onBack} data-testid="btn-back-to-url" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900">
        <ArrowLeft className="h-4 w-4" /> Start over
      </button>
      <span className="chip mt-4"><span className="h-1.5 w-1.5 rounded-full bg-indigo-600" /> Step 2 of 2 · Review your brief</span>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight text-zinc-900 mt-4 leading-[1.05]">
        Here's what we read.<br /> Nudge anything that's off.
      </h1>
      {snapshot && (
        <div className="mt-4 text-sm text-zinc-500">
          Read <a href={snapshot.url} target="_blank" rel="noreferrer" className="underline decoration-dotted text-zinc-700">{snapshot.host}</a>
          {snapshot.title && <> — <span className="text-zinc-700">{snapshot.title}</span></>}
        </div>
      )}
      {brief.inference_notes && (
        <div className="mt-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900" data-testid="inference-notes">
          <Info className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
          <div><span className="font-medium">Our assumptions:</span> {brief.inference_notes}</div>
        </div>
      )}

      <div className="mt-8 bg-white border border-zinc-200 rounded-2xl p-6 md:p-8 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Business name">
            <Input data-testid="brief-business-name" value={brief.business_name} onChange={(e) => updateBrief("business_name", e.target.value)} className="h-11" />
          </Field>
          <Field label="Business type / industry">
            <Input data-testid="brief-business-type" value={brief.business_type} onChange={(e) => updateBrief("business_type", e.target.value)} className="h-11" />
          </Field>
        </div>
        <Field label="Location">
          <Input data-testid="brief-location" value={brief.location} onChange={(e) => updateBrief("location", e.target.value)} className="h-11" />
        </Field>
        <Field label="Product / service">
          <Textarea data-testid="brief-product" value={brief.product_service} onChange={(e) => updateBrief("product_service", e.target.value)} className="min-h-20" />
        </Field>
        <Field label="Target customer">
          <Textarea data-testid="brief-target-customer" value={brief.target_customer} onChange={(e) => updateBrief("target_customer", e.target.value)} className="min-h-20" />
        </Field>
        <Field label="Unique selling point (USP)">
          <Textarea data-testid="brief-usp" value={brief.usp} onChange={(e) => updateBrief("usp", e.target.value)} className="min-h-20" />
        </Field>
        <Field label="Competitors (comma separated)">
          <Input data-testid="brief-competitors" value={brief.competitors} onChange={(e) => updateBrief("competitors", e.target.value)} className="h-11" />
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Field label="Monthly ad budget">
            <Select value={brief.monthly_budget} onValueChange={(v) => updateBrief("monthly_budget", v)}>
              <SelectTrigger data-testid="brief-budget" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BUDGET_OPTIONS.map((b) => (
                  <SelectItem key={b} value={b}>₹{Number(b).toLocaleString("en-IN")}/mo</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Primary goal">
            <Select value={brief.primary_goal} onValueChange={(v) => updateBrief("primary_goal", v)}>
              <SelectTrigger data-testid="brief-goal" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {GOALS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Brand tone">
            <Select value={brief.brand_tone} onValueChange={(v) => updateBrief("brand_tone", v)}>
              <SelectTrigger data-testid="brief-tone" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <div className="pt-4 border-t border-zinc-100 flex justify-end">
          <Button
            onClick={onBuild}
            disabled={busy}
            data-testid="btn-build-plan"
            className="btn-lift h-12 px-6 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
          >
            <Sparkles className="h-4 w-4 mr-2" /> Build my plan <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
